(ns hai.ui.components.collection.views.kanban
  "Kanban collection view: groups a collection's rows into declared, ordered
  lanes and renders them as a read-only board.

  Two parts:

  - `group-lanes` (HAI-001-01) - the pure grouping core, a function of the
    collection map. REPL-inspectable, no rendering.
  - `view` (HAI-001-02) - the board layout: an ordered column grid of lanes,
    each a header + count over a card list (or an empty-lane state), plus the
    collection's loading / error / no-items indicators. No pagination footer.

  Cards render through the collection's existing card-render hook
  (`:collection.view.card/render`), which expects a *form-wrapped* row (it reads
  the entity via `:form/current-value`). Unlike the editable table/cards views -
  which mint a real nested form per row via `form/collection` - this board is
  read-only, so it wraps each raw row in a lightweight `{:form/current-value row}`
  map rather than a live re-frame form. That is deliberate and kanban-native: a
  board of a few hundred cards has no edit state to carry, so paying for a form
  per card (and its setup effects) would be waste."
  (:require
   ["@dnd-kit/core" :refer [DndContext DragOverlay KeyboardSensor PointerSensor
                            closestCenter pointerWithin
                            useDraggable useDroppable useSensor useSensors]]
   ["react" :as react]
   [hai.entity.helpers :as entity]
   [hai.sharing.helper :as sharing]
   [hai.ui.components :as c]
   [hai.ui.components.collection.views.cards :as cards]
   [lambdaisland.ornament :as o]))

(def KanbanViewConfig
  "Dev-time config a collection declares to opt into this kanban view.

  Lives here, with the view that consumes it, but is *merged into the table
  schema* (`table/StaticTable` + `table/EditableTable`) so that
  `strip-extra-keys` keeps these keys through `decode-table-props` - otherwise
  they would be dropped as unknown extras before `group-lanes` (and the
  `settings/collection-view-options` view gating) ever saw them. All optional: a
  collection without kanban config simply omits them and keeps the default
  Table/Cards views."
  [:map
   [:collection.view.kanban/lanes
    {:optional true}
    :any]
   [:collection.view.kanban/bucket-fn
    {:optional true}
    :any]
   [:collection.view.kanban/group-by
    {:optional true}
    :any]
   ;; Injected write hook (HAI-003-03): a fn
   ;; `(fn [{:keys [row from-lane-id to-lane-id]}] ...)`. Its *presence* opts the
   ;; board into drag-to-move; absent, the board stays read-only. Declared here
   ;; so `strip-extra-keys` keeps it through `decode-table-props`.
   [:collection.view.kanban/on-card-move
    {:optional true}
    :any]])

(def view-option
  "The view-switcher descriptor for the kanban view, in the same
  `{:value :title}` shape as `settings/view-options`. A kanban board's consumer
  puts this in its `:view-options` so the generic settings machinery offers
  Kanban without settings needing any kanban-specific knowledge."
  {:value :collection.view/kanban
   :title "Kanban"})

(defn- lane-assign-fn
  "The row -> lane-id function for a collection.

  Uses `:collection.view.kanban/bucket-fn` when present (the general mapping,
  e.g. the tasks board's `task-bucket`); otherwise falls back to value-equality
  on the `:collection.view.kanban/group-by` attr. Rows are raw entities at this
  seam (the collection field's `:form.field/current-value` holds unwrapped
  maps), so both paths read entity attrs directly."
  [{:collection.view.kanban/keys [bucket-fn] group-attr :collection.view.kanban/group-by}]
  (or bucket-fn #(get % group-attr)))

(defn- catch-all-lane-id
  "Id of the lane flagged `:collection.view.kanban.lane/catch-all?`, if any.

  Optional: when a lane is so flagged, rows whose assigned lane-id matches no
  declared lane (including nil) collapse into it. With no flagged lane, such
  rows are dropped rather than dumped into an arbitrary lane."
  [lanes]
  (some (fn [{:collection.view.kanban.lane/keys [id catch-all?]}]
          (when catch-all? id))
        lanes))

(defn group-lanes
  "Group a collection's rows into ordered lanes.

  Reads rows from the collection form field
  (`:table/form` -> `:form/fields` -> `:table/rows` -> `:form.field/current-value`)
  and the `:collection.view.kanban/*` config off the collection. Pure: a
  function of the collection map, with no rendering.

  Returns a vector of
  `{:collection.view.kanban.lane/id :collection.view.kanban.lane/label
    :collection.view.kanban.lane/cards}` in declared lane order. Every declared
  lane is present, including empty ones (`:collection.view.kanban.lane/cards []`).
  Lane assignment is `bucket-fn` when supplied, else value-equality on the
  `group-by` attr. Rows whose assigned id matches no declared lane (including
  nil) collapse into the `:collection.view.kanban.lane/catch-all?` lane when one
  is declared, and are otherwise omitted."
  [{:collection.view.kanban/keys [lanes] :as collection}]
  (let [rows      (-> collection :table/form :form/fields :table/rows
                      :form.field/current-value)
        assign    (lane-assign-fn collection)
        lane-ids  (into #{} (map :collection.view.kanban.lane/id) lanes)
        catch-all (catch-all-lane-id lanes)
        by-lane   (group-by (fn [row]
                              (let [id (assign row)]
                                (cond
                                  (contains? lane-ids id) id
                                  catch-all               catch-all
                                  :else                   ::unmatched)))
                            rows)]
    (mapv (fn [{:collection.view.kanban.lane/keys [id label]}]
            {:collection.view.kanban.lane/id    id
             :collection.view.kanban.lane/label label
             :collection.view.kanban.lane/cards (vec (get by-lane id []))})
          lanes)))

(defn move-decision
  "Decide whether dropping a card from one lane onto another is a move.

  Pure: a function of the two lane-ids, the drag analogue of `group-lanes`. When
  the card lands on a different lane, returns `{:from-lane-id :to-lane-id}`; when
  it lands back on its own lane, returns `nil` (the same-lane no-op). Rendering,
  re-frame, and the actual write live elsewhere - this only names the decision so
  it is REPL-inspectable without a browser."
  [from-lane-id to-lane-id]
  (when (not= from-lane-id to-lane-id)
    {:from-lane-id from-lane-id
     :to-lane-id   to-lane-id}))

;; ---------------------------------------------------------------------------
;; Board layout (HAI-001-02)
;; ---------------------------------------------------------------------------

(o/defstyled board :div
  "The lane grid. The column count follows the number of declared lanes via the
  `--kanban-lanes` custom property (set inline by `view`) rather than an inline
  `grid-template-columns`, so the narrow-viewport collapse to a single column
  still wins (an inline `grid-template-columns` would out-specify the media
  query)."
  {:display               "grid"
   :grid-template-columns "repeat(var(--kanban-lanes, 3), minmax(0, 1fr))"
   :gap                   "20px"
   :align-items           "start"}
  [:at-media {:max-width "768px"}
   {:grid-template-columns "1fr"}])

(o/defstyled lane :section
  :flex :flex-col
  {:gap "12px"})

(o/defstyled lane-header :div
  :flex :flex-row :items-center
  {:gap "8px"})

(o/defstyled lane-label :span
  {:font-weight "600"
   :color       c/--gray-12})

(o/defstyled lane-count :span
  :inline-flex :items-center :justify-center
  {:min-width     "20px"
   :padding       "0 6px"
   :border-radius "10px"
   :font-size     "12px"
   :background    c/--gray-3
   :color         c/--gray-11})

(o/defstyled lane-cards :div
  :flex :flex-col
  {:gap "8px"})

(o/defstyled lane-empty :div
  :flex :justify-center
  {:padding   "12px"
   :color     c/--gray-9
   :font-size "14px"})

(defn- render-card
  "Render one raw lane row through the collection's card-render hook.

  `cards/card-render` dispatches to `:collection.view.card/render` when present
  (the kanban case) and expects a form-wrapped row, so wrap the raw entity in a
  lightweight `{:form/current-value row}` map - see the ns docstring for why a
  full form is not minted here."
  [collection row]
  [cards/card-render collection {:form/current-value row}])

(defn- lane-contents
  "The inner markup shared by a read-only and a droppable lane: a header (label +
  count) over the card list, or the empty-lane state. `render-card-fn` is
  `(fn [i row] -> keyed card hiccup)`, so the two lane paths differ only in how a
  card is rendered (plain vs draggable) and in their container element, not in
  this structure. Returns a seq to `into` onto the caller's container."
  [label cards render-card-fn]
  (list
   [lane-header
    [lane-label label]
    [lane-count (count cards)]]
   (if (seq cards)
     (into [lane-cards] (map-indexed render-card-fn) cards)
     [lane-empty "Nothing here."])))

(defn- lane-column
  "One read-only board column: `lane-contents` under the ornament `lane`
  container. Empty lanes still render - the declared lane set is the fixed column
  layout."
  [collection {:collection.view.kanban.lane/keys [label cards]}]
  (into [lane]
        (lane-contents label cards
                       (fn [i row]
                         ^{:key (hash [i row])}
                         [render-card collection row]))))

;; ---------------------------------------------------------------------------
;; Drag-to-move spine (HAI-003-03)
;;
;; The functional drag core: draggable cards, droppable lanes, and an
;; `onDragEnd` that invokes the injected `:collection.view.kanban/on-card-move`
;; hook on a real lane change. Active *only* when that hook is declared;
;; otherwise the board renders through the read-only path above, untouched.
;;
;; The view stays write-agnostic and speaks only in lane-ids: it reverses the
;; @dnd-kit active/over ids back to a row and a to-lane-id and hands them to the
;; consumer, which owns the actual write. @dnd-kit's hooks (`useDraggable` /
;; `useDroppable` / `useSensors`) run inside reagent `:f>` function components,
;; the repo's established React-hook interop.
;;
;; Affordances + accessibility (HAI-003-06) layer on top of this core: a
;; `DragOverlay` lifted-card ghost that follows the pointer, a hovered-lane
;; highlight while a card is over a droppable (`isOver`), a muted in-place
;; placeholder at the source (so the board does not jump), and a `KeyboardSensor`
;; with @dnd-kit screen-reader announcements for a mouse-free drag.
;; ---------------------------------------------------------------------------

(defn- card-dom-id
  "Stable @dnd-kit draggable id for a lane row: the row entity's `*/uuid` value
  as a string (@dnd-kit ids are strings/numbers). Every draggable row is an
  entity carrying a uuid attr (decision B), so this is unique and render-stable."
  [row]
  (str (get row (entity/entity->uuid-attr row))))

(defn- draggable-card*
  "Function component (`:f>`, since `useDraggable` is a hook): wraps one card in a
  @dnd-kit draggable. The hook is always called - keeping hook order stable - and
  draggability is gated by `:disabled` (from `sharing/can-edit?`), so a
  non-editable card renders inert, with no drag listeners spread onto it. While a
  card is being dragged its source stays in place and dims to a muted placeholder
  (the `DragOverlay` ghost is what follows the pointer), so the board layout does
  not jump."
  [{:keys [collection row card-id disabled?]}]
  (let [d         (useDraggable #js {:id card-id :disabled disabled?})
        dragging? (.-isDragging d)
        style     (cond-> {}
                    (not disabled?) (assoc :cursor "grab")
                    dragging?       (assoc :opacity 0.4))]
    [:div (merge {:ref   (.-setNodeRef d)
                  :style style}
                 (when-not disabled?
                   (merge (js->clj (.-attributes d) :keywordize-keys true)
                          (js->clj (.-listeners d) :keywordize-keys true))))
     [render-card collection row]]))

(defn- droppable-lane*
  "Function component (`:f>`, since `useDroppable` is a hook): one board column
  made a drop target, including when empty. The lane's `<section>` carries the
  droppable ref directly - a plain element (not the ornament `lane`) so the ref
  is guaranteed to attach to the grid-item DOM node - mirroring `lane`'s flex
  column. While a card hovers over the lane (`isOver`) its background tints to
  signal the live drop target; the padding is always present so lighting up the
  tint does not shift the layout. Cards render through `draggable-card*`."
  [{:keys [collection lane-def]}]
  (let [{:collection.view.kanban.lane/keys [id label cards]} lane-def
        d     (useDroppable #js {:id (str id)})
        over? (.-isOver d)]
    (into [:section {:ref   (.-setNodeRef d)
                     :style (cond-> {:display        "flex"
                                     :flex-direction "column"
                                     :gap            "12px"
                                     :padding        "8px"
                                     :border-radius  "8px"
                                     :transition     "background-color 120ms ease"}
                              over? (assoc :background-color (str "var(" c/--gray-3 ")")))}]
          (lane-contents label cards
                         (fn [_ row]
                           (let [cid (card-dom-id row)]
                             ^{:key cid}
                             [:f> draggable-card* {:collection collection
                                                   :row        row
                                                   :card-id    cid
                                                   :disabled?  (not (sharing/can-edit? row))}]))))))

(defn- lane-index
  "Map of droppable dom-id -> lane-id, reversing an @dnd-kit `over.id` back to a
  declared lane-id."
  [lanes]
  (into {}
        (map (fn [{id :collection.view.kanban.lane/id}] [(str id) id]))
        lanes))

(defn- card-index
  "Map of draggable dom-id -> {:row :from-lane-id}, reversing an @dnd-kit
  `active.id` back to the dragged row and the lane it started in."
  [lanes]
  (into {}
        (for [{:collection.view.kanban.lane/keys [id cards]} lanes
              row cards]
          [(card-dom-id row) {:row row :from-lane-id id}])))

(defn- lane-collision
  "Collision detection for a lane board: prefer the lane under the pointer
  (`pointerWithin`), falling back to the lane whose centre is nearest
  (`closestCenter`) when the pointer is in the gutter between lanes. The default
  `rectIntersection` resolves by the dragged card's own rectangle, which on a
  board of wide cards biases toward the source lane; pointer-first collision
  makes the drop follow the cursor."
  [args]
  (let [hits (pointerWithin args)]
    (if (pos? (.-length hits))
      hits
      (closestCenter args))))

(defn- lane-label-index
  "Map of droppable dom-id -> lane label, for turning an @dnd-kit `over.id` into a
  human lane name in screen-reader announcements."
  [lanes]
  (into {}
        (map (fn [{id    :collection.view.kanban.lane/id
                   label :collection.view.kanban.lane/label}]
               [(str id) label]))
        lanes))

(defn- drag-announcements
  "@dnd-kit screen-reader announcements for a lane drag, phrased in terms of the
  lane the card is over. `label-of` maps a droppable dom-id to its lane label; a
  drag outside any lane (nil `over`) is announced as such. Returns the JS
  announcements object @dnd-kit's accessibility layer expects."
  [label-of]
  #js {:onDragStart  (fn [_] "Picked up card.")
       :onDragOver   (fn [e]
                       (if-let [lane (some-> e .-over .-id label-of)]
                         (str "Card moved over " lane ".")
                         "Card is no longer over a lane."))
       :onDragEnd    (fn [e]
                       (if-let [lane (some-> e .-over .-id label-of)]
                         (str "Card dropped in " lane ".")
                         "Card dropped outside a lane."))
       :onDragCancel (fn [_] "Card drag cancelled.")})

(defn- lane-keyboard-coordinates
  "@dnd-kit keyboard coordinate getter for a lane board: ArrowLeft/ArrowRight jump
  the dragged card a whole lane at a time - aligning its centre to the nearest
  lane in that direction - rather than nudging the default 25px, so a keyboard
  user crosses a wide lane in one press instead of ~seventeen. Reads the live
  droppable lane rectangles (`droppableRects`) and the dragged card's current
  rectangle (`collisionRect`) from the sensor context; returns the new translate
  coordinates, or `nil` (no move) for non-horizontal keys or when there is no
  lane further in that direction. Vertical arrows are a no-op - a board's lanes
  are horizontal."
  [event args]
  (let [code    (.-code event)
        ctx     (.-context args)
        current (.-currentCoordinates args)
        coll    (some-> ctx .-collisionRect)]
    (when (and coll (or (= code "ArrowRight") (= code "ArrowLeft")))
      (let [dir     (if (= code "ArrowRight") 1 -1)
            cur-cx  (+ (.-left coll) (/ (.-width coll) 2))
            centers (volatile! [])]
        (.forEach (.-droppableRects ctx)
                  (fn [rect _id]
                    (vswap! centers conj (+ (.-left rect) (/ (.-width rect) 2)))))
        (let [ahead (filter #(pos? (* dir (- % cur-cx))) @centers)]
          (when (seq ahead)
            (let [target (apply (if (pos? dir) min max) ahead)]
              #js {:x (+ (.-x current) (- target cur-cx))
                   :y (.-y current)})))))))

(defn- dnd-board*
  "Function component (`:f>`, since `useSensors`/`useSensor` are hooks): the drag
  board. Mounts a `DndContext` with a `PointerSensor` (~6px activation distance,
  so a plain click/tap on a nested control still fires and only a real drag lifts
  the card) and a `KeyboardSensor` (Space/Enter to pick up, Left/Right arrows to
  jump lanes via `lane-keyboard-coordinates`, Space/Enter to drop) around the
  droppable lanes, plus @dnd-kit screen-reader announcements. The active card id
  is held in React state so a `DragOverlay` can render the lifted-card ghost that
  follows the pointer above the lanes. On `onDragEnd`, reverses the active/over
  ids to a row and a to-lane-id, runs the pure `move-decision`, and calls
  `on-card-move` only on an actual lane change (a same-lane drop is a no-op)."
  [{:keys [collection lanes on-card-move]}]
  (let [[active-id set-active-id!] (react/useState nil)
        sensors    (useSensors
                    (useSensor PointerSensor #js {:activationConstraint #js {:distance 6}})
                    (useSensor KeyboardSensor #js {:coordinateGetter lane-keyboard-coordinates}))
        cards      (card-index lanes)
        lane-ids   (lane-index lanes)
        label-of   (lane-label-index lanes)
        active-row (:row (get cards active-id))
        on-start   (fn [e] (set-active-id! (some-> e .-active .-id)))
        on-cancel  (fn [_] (set-active-id! nil))
        on-end     (fn [e]
                     (set-active-id! nil)
                     (let [{:keys [row from-lane-id]} (get cards (some-> e .-active .-id))
                           to-lane-id                 (get lane-ids (some-> e .-over .-id))]
                       (when (and row to-lane-id)
                         (when-let [move (move-decision from-lane-id to-lane-id)]
                           (on-card-move (assoc move :row row))))))]
    [:> DndContext {:sensors            sensors
                    :collisionDetection lane-collision
                    :accessibility      {:announcements (drag-announcements label-of)}
                    :onDragStart        on-start
                    :onDragCancel       on-cancel
                    :onDragEnd          on-end}
     ;; `:align-items "stretch"` overrides `board`'s default `start` so each
     ;; droppable lane column fills the full board height; a card can then be
     ;; dropped anywhere in a column and the hovered-lane tint reads as a
     ;; full-height drop target.
     (into [board {:style {"--kanban-lanes" (count lanes)
                           :align-items     "stretch"}}]
           (map (fn [lane-def]
                  ^{:key (:collection.view.kanban.lane/id lane-def)}
                  [:f> droppable-lane* {:collection collection :lane-def lane-def}]))
           lanes)
     ;; `:drop-animation nil` disables @dnd-kit's default drop animation, which
     ;; transitions the overlay ghost back to the *source* card's position before
     ;; unmounting. On a cross-lane move that reads as the card flying back to
     ;; where it came from; instead the ghost vanishes at the release point while
     ;; `on-card-move`'s optimistic patch has already placed the real card in the
     ;; destination lane, so the card reads as landing where it was dropped.
     [:> DragOverlay {:drop-animation nil}
      (when active-row
        [render-card collection active-row])]]))

(defn view
  "Render `collection` as a kanban board - read-only by default, drag-to-move
  when the collection declares `:collection.view.kanban/on-card-move`.

  Groups rows into declared, ordered lanes (`group-lanes`) and lays them out as
  a column grid. When no `on-card-move` hook is present each column is a plain
  read-only `lane-column`; when the hook is present the grid is wrapped by
  `dnd-board*` so cards become draggable and lanes droppable, invoking the hook
  on a cross-lane drop (see the drag-spine section above). Reuses the cards
  view's error indicator, but renders *no* loading indicator: a board's load
  transition is carried by its lanes filling in, and the bespoke tasks board
  showed no board-level spinner - a whole-board spinner over the lanes on every
  resolve would be a parity regression (surfaced in the [HAI-001-07] review;
  mirrors [HAI-001-06]'s per-lane rationale for dropping the no-items banner). An
  empty board reads as per-lane `Nothing here.` cells alone - it renders no
  collection-level no-items banner, which would double the per-lane empties (a
  board's empty state is per-lane by design; see [HAI-001-06]). Renders no
  pagination footer (a board shows its whole page) and no search bar / controls -
  those are supplied by the collection wrapper's controls, as for the table and
  cards views."
  [collection]
  (let [lanes        (group-lanes collection)
        on-card-move (:collection.view.kanban/on-card-move collection)]
    [:<>
     (if on-card-move
       [:f> dnd-board* {:collection   collection
                        :lanes        lanes
                        :on-card-move on-card-move}]
       (into [board {:style {"--kanban-lanes" (count lanes)}}]
             (map (fn [lane-def]
                    ^{:key (:collection.view.kanban.lane/id lane-def)}
                    [lane-column collection lane-def]))
             lanes))
     [cards/error-indicator collection]]))

(comment
  ;; ---------------------------------------------------------------------------
  ;; Inline testing for `group-lanes` (REPL-evaluable; verification is HITL, so
  ;; there is no automated test namespace - see the design doc's * Verification).
  ;; Eval a form, read the => expected result beneath it.
  ;; ---------------------------------------------------------------------------

  ;; Shape a collection the way `group-lanes` reads it: rows live on the
  ;; :table/rows form field's :form.field/current-value (raw entities), plus the
  ;; :collection.view.kanban/* config.
  (defn mk-collection [rows config]
    (merge {:table/form {:form/fields {:table/rows {:form.field/current-value rows}}}}
           config))

  ;; Compact view helper: [[lane-label [card-ids...]] ...]
  (defn labels+ids [collection]
    (mapv (fn [l] [(:collection.view.kanban.lane/label l)
                   (mapv :id (:collection.view.kanban.lane/cards l))])
          (group-lanes collection)))

  ;; --- Value-equality default (no bucket-fn), group-by :status ---
  (labels+ids
   (mk-collection
    [{:id 1 :status :todo} {:id 2 :status :doing}
     {:id 3 :status :todo} {:id 4 :status :done}]
    {:collection.view.kanban/group-by :status
     :collection.view.kanban/lanes
     [{:collection.view.kanban.lane/id :todo  :collection.view.kanban.lane/label "To Do"}
      {:collection.view.kanban.lane/id :doing :collection.view.kanban.lane/label "Doing"}
      {:collection.view.kanban.lane/id :done  :collection.view.kanban.lane/label "Done"}
      {:collection.view.kanban.lane/id :empty :collection.view.kanban.lane/label "Empty Lane"}]}))
  ;; => [["To Do" [1 3]] ["Doing" [2]] ["Done" [4]] ["Empty Lane" []]]
  ;; groups by attr value; empty lane present; declared order preserved.

  ;; --- bucket-fn path (mirrors hai.tasks.data/task-bucket) + catch-all lane ---
  (let [task-bucket (fn [task]
                      (case (:task/urgency task)
                        :task.urgency/now  :now
                        :task.urgency/next :next
                        :later))]
    (labels+ids
     (mk-collection
      [{:id 1 :task/urgency :task.urgency/now}
       {:id 2 :task/urgency :task.urgency/next}
       {:id 3 :task/urgency :task.urgency/now}
       {:id 4 :task/urgency nil}                   ; untriaged  -> Later
       {:id 5 :task/urgency :task.urgency/someday} ; unknown    -> Later
       {:id 6}]                                    ; missing    -> Later
      {:collection.view.kanban/bucket-fn task-bucket
       :collection.view.kanban/lanes
       [{:collection.view.kanban.lane/id :now   :collection.view.kanban.lane/label "Now"}
        {:collection.view.kanban.lane/id :next  :collection.view.kanban.lane/label "Next"}
        {:collection.view.kanban.lane/id :later :collection.view.kanban.lane/label "Later"
         :collection.view.kanban.lane/catch-all? true}]})))
  ;; => [["Now" [1 3]] ["Next" [2]] ["Later" [4 5 6]]]
  ;; nil / unknown / missing urgency all collapse into the catch-all lane.

  ;; --- No catch-all declared: unmatched / nil rows are dropped, not dumped ---
  (labels+ids
   (mk-collection
    [{:id 1 :status :todo}
     {:id 2 :status :archived}  ; matches no lane -> dropped
     {:id 3 :status nil}        ; nil             -> dropped
     {:id 4 :status :done}]
    {:collection.view.kanban/group-by :status
     :collection.view.kanban/lanes
     [{:collection.view.kanban.lane/id :todo :collection.view.kanban.lane/label "To Do"}
      {:collection.view.kanban.lane/id :done :collection.view.kanban.lane/label "Done"}]}))
  ;; => [["To Do" [1]] ["Done" [4]]]   ; :archived and nil dropped

  ;; --- Empty collection: every declared lane present, all empty ---
  (labels+ids
   (mk-collection
    []
    {:collection.view.kanban/group-by :status
     :collection.view.kanban/lanes
     [{:collection.view.kanban.lane/id :todo :collection.view.kanban.lane/label "To Do"}
      {:collection.view.kanban.lane/id :done :collection.view.kanban.lane/label "Done"}]}))
  ;; => [["To Do" []] ["Done" []]]

  ;; ---------------------------------------------------------------------------
  ;; Inline testing for `move-decision` (HAI-003-02): drop from one lane onto
  ;; another is a move; onto the same lane is a no-op.
  ;; ---------------------------------------------------------------------------

  (move-decision :now :later)
  ;; => {:from-lane-id :now :to-lane-id :later}   ; lane change -> a move

  (move-decision :now :now)
  ;; => nil                                       ; same lane -> no-op
  )
